import { ArrowRight, ShieldCheck } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isDemoMode, login, verifyLogin } from "../lib/api";
import { setDemoAuthRole } from "../lib/runtimeMode";

const defaultAdminRoute = "/admin/memory-hub/vault";
const defaultClientRoute = "/client/portal";
const memoryVaultClientRoutes = [
  "/client/dashboard",
  "/client/memory-hub/vault",
  "/client/pending",
  "/client/approved",
  "/client/featured",
  "/client/hidden",
  "/client/not-approved",
  "/client/upload-link",
  "/client/storage-billing",
  "/client/settings",
  "/client/instructions",
];

function isMemoryVaultClientRoute(pathname: string) {
  return memoryVaultClientRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

function canDisplayLocalDevelopmentCode() {
  const hostname = window.location.hostname.toLowerCase();
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname === "[::1]";
}

export function LoginPage({ admin = false }: { admin?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [message, setMessage] = useState("");
  const [helpText, setHelpText] = useState("Codes may take a few minutes. Check spam or promotions.");
  const [requestedAt, setRequestedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [resendAvailableAt, setResendAvailableAt] = useState("");
  const [clock, setClock] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [exiting, setExiting] = useState(false);
  const fromPathname = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || "";
  const isMemoryVaultLogin = !admin && isMemoryVaultClientRoute(fromPathname);
  const loginCopy = isMemoryVaultLogin
    ? {
        command: "KreatetheDate Client Vault Access",
        titleLines: ["Memory Media", "Vault"],
        tagline: "Preserve, Review, and Share Event Memories",
        confidence: "Secure access for clients reviewing event photos, videos, guest uploads, and approved Memory Hub galleries.",
        proof: ["Private Vault Access", "Encrypted One-Time Code", "Client Protected"],
        cardLabel: "KreatetheDate Access",
        subheading: "Sign in to your Memory Media Vault",
        codeButton: "Login to Memory Media Vault",
        placeholder: "client@example.com",
      }
    : {
        command: "AR Recovery Command Access",
        titleLines: ["CollabRecover", "AR Solutions"],
        tagline: "Turn Recovery Into Cash Flow",
        confidence: "Secure access for account recovery teams managing customers, documents, promises, and recovery activity.",
        proof: ["No System Access Required", "Encrypted One-Time Code", "Enterprise Protected"],
        cardLabel: "CollabRecover Access",
        subheading: "Sign in to CollabRecover AR Solutions",
        codeButton: "Login to CollabRecover",
        placeholder: "admin@example.com",
      };

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const resendDisabledUntil = useMemo(() => {
    if (!resendAvailableAt) return 0;
    const timestamp = new Date(resendAvailableAt).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }, [resendAvailableAt]);

  const resendCooldownSeconds = Math.max(0, Math.ceil((resendDisabledUntil - clock) / 1000));
  const canResend = step === "code" && !busy && resendCooldownSeconds === 0;

  const getRedirectPath = () => {
    const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
    const pathname = from?.pathname || "";
    const search = from?.search || "";
    const hash = from?.hash || "";
    if (admin && pathname.startsWith("/admin") && pathname !== "/admin/login") return `${pathname}${search}${hash}`;
    if (!admin && pathname.startsWith("/client")) return `${pathname}${search}${hash}`;
    return admin ? defaultAdminRoute : defaultClientRoute;
  };

  function resetChallengeState() {
    setCode("");
    setRequestedAt("");
    setExpiresAt("");
    setResendAvailableAt("");
    setHelpText("Codes may take a few minutes. Check spam or promotions.");
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    setMessage("");
    if (step === "code") {
      resetChallengeState();
      setStep("email");
    }
  }

  function applyChallengeResponse(result: Awaited<ReturnType<typeof login>>) {
    const nextMessage = result.devCode && canDisplayLocalDevelopmentCode()
      ? `${result.message} Local development helper: ${result.devCode}`
      : result.message;
    setMessage(nextMessage);
    setHelpText(result.helpText || "Codes may take a few minutes. Check spam or promotions.");
    setRequestedAt(result.requestedAt || "");
    setExpiresAt(result.expiresAt || "");
    setResendAvailableAt(result.resendAvailableAt || "");
    setCode("");
    setStep("code");
  }

  async function requestCode() {
    setBusy(true);
    setMessage("");
    try {
      const result = await login(email.trim(), admin);
      applyChallengeResponse(result);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to complete login.");
    } finally {
      setBusy(false);
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (step === "email") {
      await requestCode();
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      await verifyLogin(email.trim(), code, admin);
      setExiting(true);
      window.setTimeout(() => navigate(getRedirectPath(), { replace: true }), 220);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to complete login.");
    } finally {
      setBusy(false);
    }
  };

  const previewDemo = () => {
    setDemoAuthRole(admin ? "admin" : "client");
    navigate(admin ? defaultAdminRoute : defaultClientRoute);
  };

  return (
    <main className="collab-login-shell min-h-screen overflow-hidden px-5 py-8 sm:px-6" data-login-product={isMemoryVaultLogin ? "memory-vault" : "collabrecover"}>
      <div className="collab-login-bg-grid" aria-hidden="true" />
      <div className="collab-login-ambient collab-login-ambient-orange" aria-hidden="true" />
      <div className="collab-login-ambient collab-login-ambient-teal" aria-hidden="true" />
      <div className="collab-login-ambient collab-login-ambient-blue" aria-hidden="true" />

      <section className="collab-login-experience mx-auto grid min-h-[calc(100dvh-4rem)] w-full max-w-6xl items-center gap-10 lg:grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)] lg:gap-14">
        <div className="collab-login-brand-core text-center lg:text-left">
          <span className="collab-login-command-pill mb-5 inline-flex items-center gap-2">
            <span className="collab-login-command-dot" aria-hidden="true" />
            {loginCopy.command}
          </span>
          <span className="collab-login-badge mx-auto grid place-items-center overflow-hidden text-lg font-black lg:mx-0">
            {isMemoryVaultLogin ? (
              <span className="memory-vault-login-mark" aria-hidden="true">K</span>
            ) : (
              <img
                src="/assets/collabrecover-cr-logo.png"
                alt="CollabRecover AR Solutions"
                className="h-full w-full rounded-[inherit] object-cover"
              />
            )}
          </span>
          <h1 className="collab-login-title mx-auto mt-6 text-balance font-display font-bold leading-[0.98] lg:mx-0">
            {loginCopy.titleLines.map((line) => <span key={line} className="block">{line}</span>)}
          </h1>
          <p className="collab-login-tagline mt-5 font-semibold">{loginCopy.tagline}</p>
          <p className="collab-login-confidence-copy mx-auto mt-5 max-w-md text-sm font-semibold leading-7 lg:mx-0">
            {loginCopy.confidence}
          </p>
          <div className="collab-login-proof-row mt-7 flex flex-wrap justify-center gap-2 lg:justify-start">
            {loginCopy.proof.map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>

        <section className="collab-login-access-card w-full max-w-[520px] p-6 sm:p-10 lg:ml-auto" data-step={step} data-exiting={exiting ? "true" : "false"}>
          <div className="collab-login-card-chrome mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <p>{loginCopy.cardLabel}</p>
          </div>
          <Link to="/" className="focus-ring collab-login-secure-chip inline-flex items-center gap-2 px-4 py-2 text-sm font-bold transition">
            <ShieldCheck className="size-4" aria-hidden="true" />
            Secure Access
          </Link>

          <div className="mt-7">
            <h2 className="text-balance font-display text-3xl font-bold sm:text-4xl">Secure Access</h2>
            <p className="collab-login-overline mt-3 text-xs font-extrabold uppercase tracking-[0.18em]">Welcome Back</p>
            <p className="mt-2 text-sm font-semibold leading-6 text-white/62">{loginCopy.subheading}</p>
          </div>

          <form onSubmit={submit} className="mt-7 grid gap-4">
            <label className="collab-login-email-field grid gap-2 text-sm font-bold text-white/86">
              Email Address
              <input
                type="email"
                required
                value={email}
                disabled={step === "code"}
                onChange={(event) => handleEmailChange(event.target.value)}
                className="form-field collab-login-input focus-ring rounded-2xl px-4 py-3 font-medium"
                placeholder={loginCopy.placeholder}
              />
            </label>
            {step === "code" && (
              <label className="collab-login-code-field grid gap-2 text-sm font-bold text-white/86">
                One-Time Code
                <input
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="form-field collab-login-input focus-ring rounded-2xl px-4 py-3 text-lg font-bold tracking-[0.2em]"
                  placeholder="000000"
                />
              </label>
            )}
            {step === "code" && (
              <div className="collab-login-info-panel grid gap-2 rounded-2xl p-4 text-sm font-semibold">
                <p>{helpText}</p>
                {requestedAt ? <p>Code requested at: {new Date(requestedAt).toLocaleString()}</p> : null}
                {expiresAt ? <p>Code expires at: {new Date(expiresAt).toLocaleString()}</p> : null}
                {resendCooldownSeconds > 0 ? <p>Resend available in {resendCooldownSeconds} seconds.</p> : null}
              </div>
            )}
            {message && (
              <p className="collab-login-status-panel rounded-2xl p-4 text-sm font-semibold leading-6" role="status" aria-live="polite">
                {message}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="focus-ring collab-login-primary-button inline-flex min-h-12 w-full items-center justify-center gap-2 px-6 py-3 text-sm font-extrabold transition disabled:opacity-60"
            >
              {busy ? (
                <>
                  <span className="collab-login-spinner" aria-hidden="true" />
                  {step === "email" ? "Sending Secure Code..." : "Checking Access..."}
                </>
              ) : (
                <>
                  {step === "email" ? "Send Access Code" : loginCopy.codeButton}
                  <ArrowRight className="size-4" aria-hidden="true" />
                </>
              )}
            </button>
            {step === "code" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    resetChallengeState();
                    setMessage("");
                    setStep("email");
                  }}
                  disabled={busy}
                  className="focus-ring collab-login-secondary-button inline-flex min-h-12 items-center justify-center rounded-full px-5 py-3 text-sm font-extrabold transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Change Email
                </button>
                <button
                  type="button"
                  onClick={() => requestCode()}
                  disabled={!canResend}
                  className="focus-ring collab-login-secondary-button inline-flex min-h-12 items-center justify-center rounded-full px-5 py-3 text-sm font-extrabold transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? "Resending..." : resendCooldownSeconds > 0 ? `Resend in ${resendCooldownSeconds}s` : "Resend Code"}
                </button>
              </div>
            )}
            <div className="collab-login-trust-row grid grid-cols-3 gap-2 text-center text-[11px] font-extrabold uppercase tracking-[0.12em] text-white/62">
              <span>Secure</span>
              <span>One-Time Code</span>
              <span>Protected</span>
            </div>
            {isDemoMode && (
              <button
                type="button"
                onClick={previewDemo}
                className="focus-ring collab-login-secondary-button rounded-full px-5 py-3 text-sm font-extrabold"
              >
                Preview {admin ? "Admin" : "Client"} Template Locally
              </button>
            )}
          </form>
        </section>
      </section>
    </main>
  );
}
